import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Checkbox, IconButton, Snackbar, Alert, Skeleton,
} from "@mui/material";
import { MdDelete, MdVisibility } from "react-icons/md";

const ASSET_STATUSES = {
  assigned: { label: "Assigned", color: "bg-blue-100 text-blue-800" },
  in_use: { label: "In Use", color: "bg-green-100 text-green-800" },
  returned: { label: "Returned", color: "bg-gray-100 text-gray-800" },
  damaged: { label: "Damaged", color: "bg-red-100 text-red-800" },
  lost: { label: "Lost", color: "bg-red-100 text-red-800" },
};

const Assets = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [projects, setProjects] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      let url = `/api/getAllAssetAssignments?page=${page + 1}&limit=${rowsPerPage}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (projectFilter) url += `&project=${projectFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setAssignments(data.assignments || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch asset assignments", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch("/api/getAllProjects?limit=100", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setProjects(data.projects || []);
      }
    } catch (err) {
      /* silently fail */
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [page, rowsPerPage, statusFilter, projectFilter]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(assignments.map((a) => a._id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selected.length === 0) return;
    try {
      const response = await fetch("/api/deleteAssetAssignment", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: `${data.deletedCount || selected.length} assignment(s) deleted`, severity: "success" });
        setSelected([]);
        fetchAssignments();
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to delete", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete", severity: "error" });
    }
  };

  const getStatusBadge = (status) => {
    const config = ASSET_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <h2 className="text-xl font-semibold text-gray-800">Asset Assignments</h2>
        <button
          onClick={() => navigate("/Assets/Assign")}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          + Assign Asset
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          {Object.entries(ASSET_STATUSES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>

        <select
          value={projectFilter}
          onChange={(e) => { setProjectFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.projectCode} - {p.name}
            </option>
          ))}
        </select>

        {selected.length > 0 && (
          <button
            onClick={handleDelete}
            className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600"
          >
            <MdDelete /> Delete ({selected.length})
          </button>
        )}
      </div>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < assignments.length}
                    checked={assignments.length > 0 && selected.length === assignments.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Serial #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Condition</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(10)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : assignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No asset assignments found</p>
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((assignment) => (
                  <TableRow
                    key={assignment._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/Assets/View/${assignment._id}`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(assignment._id)}
                        onChange={() => handleSelect(assignment._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-primary">
                        {assignment.assetCode || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {assignment.item?.name || "N/A"}
                    </TableCell>
                    <TableCell>
                      {assignment.serialNumber || "-"}
                    </TableCell>
                    <TableCell>
                      {assignment.assignedTo?.name || "N/A"}
                    </TableCell>
                    <TableCell>
                      {assignment.project
                        ? `${assignment.project.projectCode || ""} - ${assignment.project.name || ""}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(assignment.status)}
                    </TableCell>
                    <TableCell className="capitalize">
                      {assignment.condition || "-"}
                    </TableCell>
                    <TableCell>
                      {assignment.assignedDate
                        ? new Date(assignment.assignedDate).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/Assets/View/${assignment._id}`)}
                      >
                        <MdVisibility className="text-primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Assets;
